import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DashboardEndpointDef } from '~/api/def/endpoints/dashboardEndpoint';
import {
  endpointDashboardDELETE,
  endpointDashboardGET,
  endpointDashboardGETAll,
  endpointDashboardPATCH,
  endpointDashboardPOST,
} from '~/api/def/endpoints/dashboardEndpoint';

export const useDashboards = () => {
  return useQuery({
    queryKey: ['dashboards'],
    queryFn: endpointDashboardGETAll,
  });
};

export const useDashboard = (id?: string) => {
  return useQuery({
    queryKey: ['dashboard', id],
    queryFn: () => endpointDashboardGET(id ?? ''),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  });
};

export const useCreateDashboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: endpointDashboardPOST,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });
};

export const useDeleteDashboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: endpointDashboardDELETE,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });
};

export const useUpdateDashboard = (dashboardId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      replace,
    }: {
      payload: DashboardEndpointDef['methods']['PATCH']['payload'];
      replace?: boolean;
    }) => endpointDashboardPATCH(dashboardId, payload, replace),
    onSuccess: (data, variables) => {
      // Only invalidate if it's not just a layout change
      if (variables.payload.name) {
        // If name changed, invalidate to refetch
        queryClient.invalidateQueries({
          queryKey: ['dashboard', dashboardId],
        });
      } else {
        // If only config changed, update cache optimistically
        queryClient.setQueryData(['dashboard', dashboardId], (oldData: any) => {
          if (oldData) {
            return {
              ...oldData,
              config: variables.payload.config,
            };
          }
          return oldData;
        });
      }
    },
  });
};
